/**
 * Avatar button + dropdown shown in the AppShell top nav. Surfaces
 * the user's name/email and a sign-out action. Uses a controlled
 * `<details>` for a dependency-free dropdown — Radix is not in the
 * superall deps yet, and the shell aims for a tiny bundle.
 */

import * as React from "react";
import { Link, useLocation } from "wouter";

import { useAuth } from "../providers/AuthProvider";
import { useT } from "../providers/LangProvider";

export function ProfileMenu() {
  const { user, logout } = useAuth();
  const t = useT();
  const [, setLocation] = useLocation();
  const detailsRef = React.useRef<HTMLDetailsElement | null>(null);

  // Click-outside to close the menu so it behaves like a real popover.
  React.useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const el = detailsRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) {
        el.open = false;
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const initials = (user?.fullName ?? "")
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("");

  function handleSignOut() {
    logout();
    setLocation("/login");
  }

  return (
    <details ref={detailsRef} className="relative">
      <summary
        className="flex h-9 w-9 cursor-pointer list-none items-center justify-center rounded-full bg-purple-700 text-sm font-bold text-white ring-2 ring-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
        aria-label={t.profile.menuAria}
      >
        {user?.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.fullName}
            className="h-full w-full rounded-full object-cover"
          />
        ) : (
          <span aria-hidden="true">{initials || "?"}</span>
        )}
      </summary>

      <div
        className="absolute end-0 top-11 z-20 w-60 rounded-xl border border-neutral-200 bg-white p-2 shadow-lg"
        role="menu"
      >
        {user && (
          <div className="border-b border-neutral-100 px-3 py-2">
            <p className="truncate text-sm font-semibold text-neutral-900">
              {user.fullName}
            </p>
            <p className="truncate text-xs text-neutral-500">{user.email}</p>
          </div>
        )}
        <Link
          href="/profile"
          className="block rounded-lg px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 focus-visible:bg-neutral-100"
          role="menuitem"
        >
          {t.profile.viewProfile}
        </Link>
        <button
          type="button"
          onClick={handleSignOut}
          role="menuitem"
          className="w-full rounded-lg px-3 py-2 text-start text-sm text-red-600 hover:bg-red-50 focus-visible:bg-red-100"
          aria-label={t.signOutAria}
        >
          {t.signOut}
        </button>
      </div>
    </details>
  );
}
